import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import type { TranslateFn } from "../../i18n";
import type { Profile, ProfileMeasurements, ProfileCompletionDraft, ChestProfile, MidsectionProfile, HipProfile } from "../types";
import { BODY_IMAGES } from "../assets/body-illustrations-png";
import { compressImage, checkAgeBeforeUpload } from "../../image-utils";
import { detectBodyLandmarks, type BodyLandmarks } from "../../pose-detect";
import { useIsMobile } from "../utils/useIsMobile";
import { PoseOverlay } from "./PoseOverlay";
import { MobileScanningView } from "./MobileScanningView";
import { PhotoStepMobile } from "./PhotoStepMobile";
import { ProfileMeasurementsView } from "./ProfileMeasurementsView";
import { UploadIcon } from "../icons";
// Inlined Gemini-generated illustrations for the method picker cards.
// `assetsInlineLimit` in vite.config.ts is large enough to base64 these.
import methodManualImg from "../assets/method-manual.png";
import methodPhotoImg from "../assets/method-photo.png";

/**
 * Multi-step profile creation wizard.
 *
 * NEW FLOW (Apr 2026):
 *
 *   1. Method picker — user chooses between:
 *        a) "Measure manually"  — enter height, weight, and answer body
 *           shape questions one at a time with fade transitions.
 *        b) "Analyze by photo"  — pick a profile name, upload a full-body
 *           photo, and let the SDK estimate every measurement (waist, hip,
 *           sleeve, inseam, chest, etc.) via vision.
 *
 *   2a. MANUAL PATH
 *        Step "identity"      — name + gender + units + height + weight + age
 *        Step "chest" (men)   — single ShapeCard row, fade transition
 *        Step "stomach"       — single ShapeCard row, fade transition
 *        Step "seat"          — single ShapeCard row, fade transition
 *        Step "hips" (women)  — single ShapeCard row, fade transition
 *        Step "bra" (women)   — band + cup pickers
 *        → Save → parent persists + runs estimateFullMeasurements
 *
 *   2b. IMAGE PATH
 *        Step "name"   — profile name + gender (no measurements needed)
 *        Step "photo"  — full-screen photo upload (same UI as the SDK's
 *                         "Find size by photo" mode)
 *        → Save with photoBase64 → parent's onSaveNewProfile already calls
 *           estimateFullMeasurements({bodyImage}) which kicks off the
 *           vision pipeline and stores all body measurements on the profile.
 *
 * Body shape questions are rendered inside `ProgressiveStep`, which keys
 * the children on the active step name so changing step triggers a CSS
 * fade-in animation.
 */

interface CreateProfileWizardProps {
  onSave: (data: Omit<Profile, "id" | "createdAt">) => void | Promise<void>;
  onCancel: () => void;
  initialMode?: "manual" | "image";
  initialDraft?: ProfileCompletionDraft | null;
  /** Direct profile-save path launched from a size result. Keeps the shell back button and skips wizard chrome. */
  directAnalysisFlow?: boolean;
  /** Backend URL + API key — required for the pre-upload age check call. */
  apiUrl?: string;
  apiKey?: string;
  /** Called when a photo is selected (passes base64) or removed (passes null).
   *  Host can use this to mirror the photo in the product-image left panel. */
  onPhotoPreview?: (base64: string | null) => void;
  /** Called during image path to estimate body measurements from photo + height/weight.
   *  Returns estimates that get saved to the profile. Wizard shows a calculating
   *  animation while this runs. */
  onEstimate?: (data: {
    photoBase64: string;
    height: number;
    weight: number;
    heightUnit: "cm" | "in";
    weightUnit: "kg" | "lbs";
    gender: "male" | "female";
    age?: number;
    bandSize?: string;
    cupSize?: string;
    braSizeRegion?: string;
    bodyLandmarks?: Record<string, { x: number; y: number }> | null;
  }) => Promise<{ estimates: Record<string, number>; unit: string } | null>;
  t: TranslateFn;
}

type WizardMode = null | "manual" | "image";
type ImageStep = "name-photo" | "details" | "calculating";

// Manual flow steps. Order is gender-aware: men get "chest", women get
// "hips" + "bra"; both share basics + stomach + seat.
type ManualStep = "identity" | "chest" | "stomach" | "seat" | "hips" | "bra";
// Image flow is a single combined screen now: gender + name + photo all
// visible at once. No more two-step image flow.

// Bra size data by region — mirrors BodyProfileView so the image-flow bra
// inputs honour the same region-specific options.
const BAND_SIZES: Record<string, string[]> = {
  US: ["28","30","32","34","36","38","40","42","44","46","48","50","52","54","56"],
  UK: ["28","30","32","34","36","38","40","42","44","46","48","50","52","54","56"],
  EU: ["60","65","70","75","80","85","90","95","100","105","110","115","120"],
  FR: ["80","85","90","95","100","105","110","115","120"],
  IT: ["0","1","2","3","4","5","6","7","8","9","10"],
  JP: ["60","65","70","75","80","85","90","95","100"],
};
const CUP_SIZES: Record<string, string[]> = {
  US: ["AA","A","B","C","D","DD/E","DDD/F","G","H","I","J"],
  UK: ["AA","A","B","C","D","DD","E","F","FF","G","GG","H"],
  EU: ["AA","A","B","C","D","E","F","G","H","I","J"],
  FR: ["AA","A","B","C","D","E","F","G","H","I","J"],
  IT: ["AA","A","B","C","D","DD","E","F","G","H","I"],
  JP: ["AA","A","B","C","D","E","F","G","H","I"],
};

function draftNumber(value: unknown): number | null {
  const n = typeof value === "number" ? value : parseFloat(String(value ?? ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function draftDisplayNumber(value: unknown): string {
  const n = draftNumber(value);
  if (n == null) return "";
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10);
}

function hasUsableLegLandmarks(landmarks: BodyLandmarks | null | undefined): boolean {
  if (!landmarks) return false;
  const isVisible = (point: unknown): point is { x: number; y: number; visibility?: number } => {
    if (!point || typeof point !== "object") return false;
    const p = point as { x?: unknown; y?: unknown; visibility?: unknown };
    if (typeof p.x !== "number" || typeof p.y !== "number") return false;
    if (p.x < 0.001 && p.y < 0.001) return false;
    return typeof p.visibility !== "number" || p.visibility >= 0.08;
  };
  const lowerBodyPoints = [
    landmarks.leftKnee,
    landmarks.rightKnee,
    landmarks.leftAnkle,
    landmarks.rightAnkle,
  ];
  const visibleLowerBodyCount = lowerBodyPoints.filter(isVisible).length;
  const shoulderYs = [landmarks.leftShoulder, landmarks.rightShoulder]
    .filter(isVisible)
    .map((point) => point.y);
  const ankleYs = [landmarks.leftAnkle, landmarks.rightAnkle]
    .filter(isVisible)
    .map((point) => point.y);
  const verticalSpan = shoulderYs.length && ankleYs.length
    ? Math.max(...ankleYs) - Math.min(...shoulderYs)
    : 0;
  return visibleLowerBodyCount >= 3 && verticalSpan > 0.32;
}

function draftUnit(draft?: ProfileCompletionDraft | null): "cm" | "in" {
  const heightUnit = String(draft?.heightUnit || "").toLowerCase();
  const weightUnit = String(draft?.weightUnit || "").toLowerCase();
  if (heightUnit === "in" || heightUnit === "ft" || weightUnit === "lbs") return "in";
  if (heightUnit === "cm" || weightUnit === "kg") return "cm";
  return "in";
}

function draftHeightParts(draft: ProfileCompletionDraft | null | undefined, unit: "cm" | "in") {
  const h = draftNumber(draft?.height);
  if (h == null) return { heightVal: "", heightFt: "", heightInch: "" };
  if (unit === "cm") return { heightVal: draftDisplayNumber(h), heightFt: "", heightInch: "" };
  const rawHeightUnit = String(draft?.heightUnit || "").toLowerCase();
  const totalInches = rawHeightUnit === "cm" ? h / 2.54 : h;
  const ft = Math.floor(totalInches / 12);
  const inch = Math.max(0, Math.round(totalInches - ft * 12));
  return { heightVal: "", heightFt: String(ft), heightInch: String(Math.min(11, inch)) };
}

function cleanNumericText(value: string, allowDecimal = false): string {
  const cleaned = value.replace(allowDecimal ? /[^0-9.]/g : /\D/g, "");
  if (!allowDecimal) return cleaned;
  const [head = "", ...rest] = cleaned.split(".");
  return rest.length ? `${head}.${rest.join("")}` : head;
}

/**
 * Custom animated mini-select drawer used by the image-flow bra inputs.
 * Mirrors the BodyProfileView component so the visual treatment matches
 * across both upload entry points.
 */
function MiniSelect({
  value, options, placeholder, isOpen, onToggle, onSelect, direction = "up",
}: {
  value: string | null;
  options: string[];
  placeholder?: string;
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (v: string) => void;
  direction?: "up" | "down";
}) {
  const handleToggle = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onToggle();
  };

  const handleSelect = (event: ReactMouseEvent<HTMLButtonElement> | ReactPointerEvent<HTMLButtonElement>, option: string) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect(option);
  };

  return (
    <div className="ps-bp-mini-select" data-open={isOpen ? "true" : "false"} data-dir={direction}>
      <button
        type="button"
        className="ps-bp-mini-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={handleToggle}
      >
        <span className="ps-bp-mini-select-value">
          {value || <span className="ps-bp-mini-select-placeholder">{placeholder ?? "—"}</span>}
        </span>
        <span className={`ps-bp-mini-select-arrow${isOpen ? " ps-open" : ""}`}>&#9662;</span>
      </button>
      {isOpen && (
        <div className="ps-bp-mini-select-panel" role="listbox">
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              role="option"
              aria-selected={value === opt}
              className={`ps-bp-mini-select-item${value === opt ? " ps-selected" : ""}`}
              onPointerDown={(event) => handleSelect(event, opt)}
              onClick={(event) => handleSelect(event, opt)}
            >{opt}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function ShapeCard({
  img, label, selected, onSelect,
}: {
  img: string | null;
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`ps-cpw-card${selected ? " ps-active" : ""}`}
    >
      <div className="ps-cpw-card-thumb">
        {img && <img src={img} alt={label} />}
      </div>
      <span className="ps-cpw-card-label">{label}</span>
    </button>
  );
}

/**
 * Wraps a single body-shape question in a fading container. The `key`
 * prop bumps every time the active step changes, which forces React to
 * unmount and remount this subtree — combined with the `ps-cpw-fade-in`
 * CSS class, that produces the fade-up animation between questions.
 */
function ProgressiveStep({
  stepKey,
  title,
  subtitle,
  children,
}: {
  stepKey: string;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div key={stepKey} className="ps-cpw-progressive ps-cpw-fade-in">
      <div className="ps-cpw-section-head">
        <div className="ps-cpw-section-title">{title}</div>
        <div className="ps-cpw-section-sub">{subtitle}</div>
      </div>
      <div className="ps-cpw-card-row">{children}</div>
    </div>
  );
}

export function CreateProfileWizard({ onSave, onCancel, initialMode, initialDraft, directAnalysisFlow = false, apiUrl, apiKey, onPhotoPreview, onEstimate, t }: CreateProfileWizardProps) {
  const isMobile = useIsMobile();
  const seededUnit = draftUnit(initialDraft);
  const seededHeight = draftHeightParts(initialDraft, seededUnit);
  const seededGender = initialDraft?.gender === "female" ? "female" : "male";
  const seededPhotoRef = useRef<string | null>(initialDraft?.photoBase64 || initialDraft?.photoUrl || null);
  const seededMeasurements = initialDraft?.measurements && Object.keys(initialDraft.measurements).length > 0
    ? initialDraft.measurements
    : null;
  const seededMeasurementsUnit = initialDraft?.measurementsUnit === "in" ? "in" : "cm";

  const [mode, setMode] = useState<WizardMode>(initialMode ?? null);
  const [manualStep, setManualStep] = useState<ManualStep>("identity");
  const [imageStep, setImageStep] = useState<ImageStep>("name-photo");
  const [estimating, setEstimating] = useState(false);
  const [estimateResults, setEstimateResults] = useState<Record<string, number> | null>(null);
  const [estimateUnit, setEstimateUnit] = useState<"cm" | "in">(seededMeasurementsUnit);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  // Reset scroll on step/mode changes
  useEffect(() => {
    if (bodyScrollRef.current) bodyScrollRef.current.scrollTop = 0;
  }, [imageStep, manualStep, mode]);

  // ── Identity (used by both manual and image paths) ──
  const [name, setName] = useState(initialDraft ? "My Fit Profile" : "");
  const [gender, setGender] = useState<"male" | "female">(seededGender);
  const [unit, setUnit] = useState<"cm" | "in">(seededUnit);
  const [heightVal, setHeightVal] = useState(seededHeight.heightVal);
  // Split ft/in fields for imperial height entry
  const [heightFt, setHeightFt] = useState(seededHeight.heightFt);
  const [heightInch, setHeightInch] = useState(seededHeight.heightInch);
  const [weightVal, setWeightVal] = useState(draftDisplayNumber(initialDraft?.weight));
  const [ageVal, setAgeVal] = useState(draftDisplayNumber(initialDraft?.age));
  const [error, setError] = useState("");

  // ── Photo state (image path only) ──
  const [photoBase64, setPhotoBase64] = useState<string | null>(() => seededPhotoRef.current);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState<boolean | null>(null);
  const [uploadHoverCpw, setUploadHoverCpw] = useState(false);
  // Status text shown in the upload-zone overlay while we're processing the
  // file (compress + age check). Mirrors BodyProfileView so both upload
  // entry points behave identically.
  const [photoStatus, setPhotoStatus] = useState<string>("");
  // Set when the age-check rejects the photo — shows in the same slot as
  // the "Are you 18+?" chip, with the rejection reason from Gemini.
  const [photoRejection, setPhotoRejection] = useState<string | null>(null);
  useEffect(() => {
    if (seededPhotoRef.current) onPhotoPreview?.(seededPhotoRef.current);
  }, []);
  // MediaPipe joint landmarks, displayed as a skeleton overlay on the
  // photo during the "calculating" step so the user sees visual proof
  // the AI is analysing their photo (not just a generic spinner).
  const [scanLandmarks, setScanLandmarks] = useState<BodyLandmarks | null>(null);
  const [scanImgDims, setScanImgDims] = useState<{ w: number; h: number }>({ w: 800, h: 1200 });
  const scanDetectionPhotoRef = useRef<string | null>(null);
  const scanDetectionPromiseRef = useRef<Promise<BodyLandmarks | null> | null>(null);
  // Cycling stage index for the animated status text on the right of
  // the scanning view. Advances every ~1.5s while `estimating` is true.
  const [scanStageIdx, setScanStageIdx] = useState(0);
  // Photo-tips popover visibility — toggled by the "?" pill button in
  // the header of the photo step's right column. Same pattern as
  // BodyProfileView.
  const [photoHelpOpen, setPhotoHelpOpen] = useState(false);
  const directAutoStartedRef = useRef(false);
  const detectProfileBodyLandmarks = useCallback((): Promise<BodyLandmarks | null> => {
    if (!photoBase64) return Promise.resolve(null);
    if (scanDetectionPhotoRef.current !== photoBase64 || !scanDetectionPromiseRef.current) {
      scanDetectionPhotoRef.current = photoBase64;
      scanDetectionPromiseRef.current = detectBodyLandmarks(photoBase64, { maxLongEdge: 1600 }).catch(() => null);
    }
    return scanDetectionPromiseRef.current.then((landmarks) => {
      if (scanDetectionPhotoRef.current === photoBase64 && landmarks) {
        setScanLandmarks(landmarks);
      }
      return landmarks;
    });
  }, [photoBase64]);

  // Detect joints on the photo once we enter the calculating step, so
  // the skeleton can overlay the image while estimation runs.
  useEffect(() => {
    if (imageStep !== "calculating" || !photoBase64) return;
    let cancelled = false;
    detectProfileBodyLandmarks()
      .then((lm) => { if (!cancelled && lm) setScanLandmarks(lm); })
      .catch(() => { /* non-fatal — skeleton just won't render */ });
    return () => { cancelled = true; };
  }, [detectProfileBodyLandmarks, imageStep, photoBase64]);

  // Stage cadence — mirrors MobileScanningView (1800 ms dwell, last stage
  // sticky). Reset to 0 every time the calculating step opens so stages
  // start from "DETECTING POSE" on each run instead of resuming where the
  // previous run left off.
  useEffect(() => {
    if (imageStep !== "calculating") return;
    setScanStageIdx(0);
  }, [imageStep]);
  useEffect(() => {
    if (imageStep !== "calculating") return;
    // Stop advancing once we hit the last stage (5 stages total — see the
    // render block below). Re-arms via the reset effect above when the
    // user kicks off a new estimation.
    const STAGE_DWELL_MS = 1800;
    const TOTAL_STAGES = 5;
    if (scanStageIdx >= TOTAL_STAGES - 1) return;
    const id = setTimeout(
      () => setScanStageIdx((i) => Math.min(TOTAL_STAGES - 1, i + 1)),
      STAGE_DWELL_MS,
    );
    return () => clearTimeout(id);
  }, [imageStep, scanStageIdx]);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [nameShaking, setNameShaking] = useState(false);
  const handlePhotoFile = async (file: File, options: { requireAgeConfirmation?: boolean } = {}) => {
    if (options.requireAgeConfirmation && ageConfirmed !== true) {
      setError(t("Please confirm that the person in the photo is 18 or older before uploading."));
      return;
    }
    if (!file.type.startsWith("image/")) {
      setError(t("Please upload an image file"));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(t("Image must be under 10MB"));
      return;
    }
    setError("");
    setPhotoUploading(true);
    setPhotoStatus(t("Analyzing photo…"));
    try {
      // Run age-check (tight 384px thumb) and main compression in parallel
      // so we're bounded by whichever finishes last — not the sum.
      const ageCheckPromise: Promise<{ isAdult: boolean; confidence: "high" | "low"; reasoning?: string }> =
        apiUrl && apiKey
          ? checkAgeBeforeUpload(file, apiUrl, apiKey)
          : Promise.resolve({ isAdult: true, confidence: "low" });
      // Use high-quality compression for profile photos. The default
       // (0.65 / 1024px) gave visibly blurry results when the photo was
       // later displayed in the profile detail view or restored from
       // IndexedDB. 0.92 + 1280 keeps the photo visually crisp at the
       // sizes the SDK ever shows it. Same quality the body-profile
       // upload flow uses, so all paths agree.
      const compressPromise = compressImage(file, { maxDimension: 1280, quality: 0.92 });

      const ageResult = await ageCheckPromise;
      if (!ageResult.isAdult) {
        // Set photoRejection (not setError) — UI shows the rejection reason
        // in the same slot as the "Are you 18+?" prompt instead of a generic
        // toast. Falls back to a polite default if no reasoning provided.
        const reason = ageResult.reasoning?.trim() || t("This photo appears to be of a minor. Please upload a photo of someone 18 or older.");
        setPhotoRejection(reason);
        return; // do NOT call setPhotoBase64 — image is dropped here, never sent further
      }

      const base64 = await compressPromise;
      setPhotoBase64(base64);
      setScanLandmarks(null);
      scanDetectionPhotoRef.current = null;
      scanDetectionPromiseRef.current = null;
      setEstimateResults(null);
      setEstimateUnit("cm");
      setPhotoRejection(null);
      onPhotoPreview?.(base64);
    } catch {
      setError(t("Failed to process image"));
    } finally {
      setPhotoUploading(false);
      setPhotoStatus("");
    }
  };
  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handlePhotoFile(file, { requireAgeConfirmation: true });
  };
  const handleRemovePhoto = () => {
    setPhotoBase64(null);
    setScanLandmarks(null);
    scanDetectionPhotoRef.current = null;
    scanDetectionPromiseRef.current = null;
    setEstimateResults(null);
    setEstimateUnit("cm");
    onPhotoPreview?.(null);
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  // ── Body shape state (manual path) ──
  const [chestProfile, setChestProfile] = useState<ChestProfile | null>(initialDraft?.chestProfile ?? null);
  const [midsectionProfile, setMidsectionProfile] = useState<MidsectionProfile | null>(initialDraft?.midsectionProfile ?? null);
  const [seatProfile, setSeatProfile] = useState<HipProfile | null>(seededGender === "male" ? (initialDraft?.hipProfile ?? null) : null);
  const [hipProfile, setHipProfile] = useState<HipProfile | null>(seededGender === "female" ? (initialDraft?.hipProfile ?? null) : null);

  // ── Bra band + cup (women only) ──
  const [bandSize, setBandSize] = useState<string | null>(initialDraft?.bandSize ?? null);
  const [cupSize, setCupSize] = useState<string | null>(initialDraft?.cupSize ?? null);
  const [braSizeRegion, setBraSizeRegion] = useState<string>(
    initialDraft?.braSizeRegion && BAND_SIZES[initialDraft.braSizeRegion]
      ? initialDraft.braSizeRegion
      : "US",
  );
  // Tracks which inline mini-select is open in the image-flow bra row
  // (region | band | cup | null). Single state so opening one closes the
  // others. Click-outside closes via effect below.
  const [inlineBraOpen, setInlineBraOpen] = useState<"region" | "band" | "cup" | null>(null);
  const inlineBraWrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!inlineBraOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const wrap = inlineBraWrapRef.current;
      if (wrap && !wrap.contains(e.target as Node)) setInlineBraOpen(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [inlineBraOpen]);
  const BAND_OPTIONS_US = ["28", "30", "32", "34", "36", "38", "40", "42", "44", "46", "48", "50", "52", "54", "56"];
  const CUP_OPTIONS_US = ["AA", "A", "B", "C", "D", "DD/E", "DDD/F", "G", "H", "I", "J"];

  const isWomen = gender === "female";
  const imgs = (isWomen ? BODY_IMAGES.female : BODY_IMAGES.male) as Record<string, Record<string, string>>;

  // Compute the manual flow step order based on gender. Bra band/cup
  // for women is collected inline on the identity step (mirrors the
  // SDK's photo-path body-details column), so there's no separate
  // "bra" screen any more — saves a click.
  const manualStepOrder: ManualStep[] = isWomen
    ? ["identity", "stomach", "seat", "hips"]
    : ["identity", "chest", "stomach", "seat"];
  const manualStepIdx = manualStepOrder.indexOf(manualStep);
  const manualStepCount = manualStepOrder.length;
  const isLastManualStep = manualStepIdx === manualStepCount - 1;

  // ── Unit toggle with ft/cm conversion ──
  const handleSetUnitCm = () => {
    if (unit === "in") {
      const ft = parseInt(heightFt, 10);
      const inch = parseInt(heightInch, 10);
      if (!isNaN(ft) && ft >= 0 && !isNaN(inch) && inch >= 0) {
        setHeightVal(String(Math.round((ft * 12 + inch) * 2.54)));
      }
    }
    setUnit("cm");
  };
  const handleSetUnitIn = () => {
    if (unit === "cm") {
      const cm = parseFloat(heightVal);
      if (!isNaN(cm) && cm > 0) {
        setHeightFt(String(Math.floor(cm / 2.54 / 12)));
        setHeightInch(String(Math.round((cm / 2.54) % 12)));
      }
    }
    setUnit("in");
  };

  // ── Validation ──
  const validateIdentity = (): boolean => {
    if (!name.trim()) { setError(t("Please enter a profile name")); return false; }
    let heightValid: boolean;
    if (unit === "in") {
      const ft = parseInt(heightFt, 10);
      const inch = parseInt(heightInch, 10);
      heightValid = !isNaN(ft) && ft > 0 && !isNaN(inch) && inch >= 0 && inch <= 11;
    } else {
      const h = parseFloat(heightVal);
      heightValid = !isNaN(h) && h > 0;
    }
    if (!heightValid) { setError(t("Please enter a valid height")); return false; }
    const w = parseFloat(weightVal);
    if (!w || w <= 0) { setError(t("Please enter a valid weight")); return false; }
    const a = parseFloat(ageVal);
    if (!a || a <= 0) { setError(t("Please enter a valid age")); return false; }
    if (isWomen && (!bandSize || !cupSize)) {
      setError(t("Please select your bra band and cup size"));
      return false;
    }
    setError("");
    return true;
  };

  const validateNameOnly = (): boolean => {
    if (!name.trim()) { setError(t("Please enter a profile name")); return false; }
    setError("");
    return true;
  };

  // ── Save helpers ──
  const buildManualPayload = (): Omit<Profile, "id" | "createdAt"> => {
    const heightRaw = unit === "in"
      ? (parseInt(heightFt, 10) || 0) * 12 + (parseInt(heightInch, 10) || 0)
      : parseFloat(heightVal);
    const a = parseFloat(ageVal);
    return {
      name: name.trim(),
      gender,
      sizingUnit: unit,
      heightUnit: unit,
      weightUnit: unit === "in" ? "lbs" : "kg",
      heightCm: heightRaw,
      weightKg: parseFloat(weightVal),
      height: heightRaw,
      weight: parseFloat(weightVal),
      ...(a > 0 ? { age: a } : {}),
      chestProfile: chestProfile || undefined,
      midsectionProfile: midsectionProfile || undefined,
      hipProfile: isWomen ? (hipProfile || undefined) : (seatProfile || undefined),
      bandSize: isWomen ? (bandSize || undefined) : undefined,
      cupSize: isWomen ? (cupSize || undefined) : undefined,
      ...(isWomen && bandSize && cupSize ? { braSizeRegion } as any : {}),
      ...(initialDraft?.measurements ? { measurements: initialDraft.measurements, measurementsUnit: initialDraft.measurementsUnit || "cm" } : {}),
      ...(initialDraft?.customMeasurements ? { customMeasurements: initialDraft.customMeasurements } : {}),
    };
  };

  const buildImagePayload = (): Omit<Profile, "id" | "createdAt"> => {
    // For image path, height/weight are still recorded if the user filled
    // them in (they live in the same identity inputs), but they're not
    // required — the vision estimator can infer everything from the photo.
    // Either way, photoBase64 is the trigger that flips the parent into
    // vision-mode estimation in onSaveNewProfile.
    const heightRaw = unit === "in"
      ? (parseInt(heightFt, 10) || 0) * 12 + (parseInt(heightInch, 10) || 0)
      : parseFloat(heightVal);
    const w = parseFloat(weightVal);
    const a = parseFloat(ageVal);
    return {
      name: name.trim(),
      gender,
      sizingUnit: unit,
      heightUnit: unit,
      weightUnit: unit === "in" ? "lbs" : "kg",
      ...(heightRaw > 0 ? { heightCm: heightRaw, height: heightRaw } : {}),
      ...(w > 0 ? { weightKg: w, weight: w } : {}),
      ...(a > 0 ? { age: a } : {}),
      ...(isWomen && bandSize ? { bandSize } : {}),
      ...(isWomen && cupSize ? { cupSize } : {}),
      ...(isWomen && bandSize && cupSize ? { braSizeRegion } as any : {}),
      ...(initialDraft?.measurements ? { measurements: initialDraft.measurements, measurementsUnit: initialDraft.measurementsUnit || "cm" } : {}),
      ...(initialDraft?.customMeasurements ? { customMeasurements: initialDraft.customMeasurements } : {}),
      photoBase64: photoBase64 || undefined,
    };
  };

  const buildImageReviewProfile = (): Profile | null => {
    if (!estimateResults) return null;
    const payload = buildImagePayload();
    return {
      ...(payload as Profile),
      id: "draft-profile-review",
      createdAt: initialDraft?.createdAt ?? 0,
      measurements: estimateResults as ProfileMeasurements,
      measurementsUnit: estimateUnit,
    };
  };

  const saveImageProfile = (
    measurements?: ProfileMeasurements,
    measurementsUnit?: "cm" | "in",
    profileDetails?: { bandSize?: string; cupSize?: string },
  ) => {
    const payload = buildImagePayload();
    const nextMeasurements = measurements ?? (estimateResults as ProfileMeasurements | null);
    if (nextMeasurements && Object.keys(nextMeasurements).length > 0) {
      payload.measurements = nextMeasurements;
      payload.measurementsUnit = measurementsUnit ?? estimateUnit;
    }
    if (isWomen && profileDetails) {
      payload.bandSize = profileDetails.bandSize || undefined;
      payload.cupSize = profileDetails.cupSize || undefined;
      if (profileDetails.bandSize && profileDetails.cupSize) payload.braSizeRegion = braSizeRegion;
    }
    onSave(payload);
  };

  // ── Manual flow navigation ──
  const advanceManual = () => {
    if (manualStep === "identity") {
      if (!validateIdentity()) return;
    }
    if (manualStep === "chest" && !chestProfile) {
      setError(t("Please pick an option"));
      return;
    }
    if (manualStep === "stomach" && !midsectionProfile) {
      setError(t("Please pick an option"));
      return;
    }
    if (manualStep === "seat" && !seatProfile) {
      setError(t("Please pick an option"));
      return;
    }
    if (manualStep === "hips" && !hipProfile) {
      setError(t("Please pick an option"));
      return;
    }
    if (manualStep === "bra" && (!bandSize || !cupSize)) {
      setError(t("Please pick band and cup size"));
      return;
    }
    setError("");
    if (isLastManualStep) {
      onSave(buildManualPayload());
      return;
    }
    setManualStep(manualStepOrder[manualStepIdx + 1]!);
  };

  // Picking a body-shape card auto-advances to the next step (or submits
  // when the picked step is the last manual step). A short visual delay
  // lets the active-card animation show before the fade-out kicks in.
  // We pass the picked value through directly so React's batched state
  // updates don't lose it before buildManualPayload runs on the last step.
  const pickShape = (
    step: "chest" | "stomach" | "seat" | "hips",
    value: ChestProfile | MidsectionProfile | HipProfile,
  ) => {
    setError("");
    if (step === "chest") setChestProfile(value as ChestProfile);
    else if (step === "stomach") setMidsectionProfile(value as MidsectionProfile);
    else if (step === "seat") setSeatProfile(value as HipProfile);
    else if (step === "hips") setHipProfile(value as HipProfile);

    const idx = manualStepOrder.indexOf(step as ManualStep);
    const isLast = idx === manualStepCount - 1;

    setTimeout(() => {
      if (isLast) {
        // Last step's value isn't committed yet — build the payload and
        // override the field that was just picked.
        const payload = buildManualPayload();
        if (step === "chest") payload.chestProfile = value as ChestProfile;
        else if (step === "stomach") payload.midsectionProfile = value as MidsectionProfile;
        else if (step === "seat") payload.hipProfile = value as HipProfile; // men only
        else if (step === "hips") payload.hipProfile = value as HipProfile;
        onSave(payload);
      } else {
        setManualStep(manualStepOrder[idx + 1]!);
      }
    }, 220);
  };

  const goBackManual = () => {
    setError("");
    if (manualStep === "identity") {
      // Back from first step → mode picker
      setMode(null);
      return;
    }
    setManualStep(manualStepOrder[Math.max(0, manualStepIdx - 1)]!);
  };

  // Steps where the Next button is hidden (the card pick auto-advances).
  const isAutoAdvanceStep =
    mode === "manual" &&
    (manualStep === "chest" || manualStep === "stomach" || manualStep === "seat" || manualStep === "hips");

  // ── Image flow — simplified: name+photo+h/w/a → calculating ──
  // (The old separate "details" step has been merged into "name-photo"
  // so the user fills in name, height, weight, age and uploads a photo
  // on a single screen, then we calculate.)
  const advanceImage = async () => {
    if (imageStep === "name-photo") {
      if (!validateNameOnly()) return;
      if (!photoBase64) {
        setError(t("Please upload a photo"));
        return;
      }
      const canUseSeededMeasurements = directAnalysisFlow && !!seededMeasurements;
      // Height is required for the landmark anchor scaling.
      if (!canUseSeededMeasurements && unit === "in") {
        const ft = parseFloat(heightFt);
        if (!ft || ft < 3 || ft > 8) { setError(t("Please enter a valid height")); return; }
      } else if (!canUseSeededMeasurements) {
        const cm = parseFloat(heightVal);
        if (!cm || cm < 100 || cm > 250) { setError(t("Please enter a valid height")); return; }
      }
      const w = parseFloat(weightVal);
      if (!canUseSeededMeasurements && (!w || w <= 0)) { setError(t("Please enter a valid weight")); return; }
      // Age is intentionally NOT required in the image-upload path —
      // the vision pipeline doesn't need it (height / weight / photo
      // already constrain the body model). We only require it on the
      // manual entry path where the population formula uses it.
      const a = parseFloat(ageVal);
      if (isWomen && !directAnalysisFlow && (!bandSize || !cupSize)) {
        setError(t("Please select your bra band and cup size"));
        return;
      }
      setError("");
      setImageStep("calculating");
      setEstimating(true);

      // Run estimation alongside a 6-second minimum hold so the user
      // always sees the full DETECTING → SCANNING → ANALYZING → MATCHING
      // → FINALIZING cadence — matches the normal AI sizing experience
      // even when the backend resolves in 200 ms.
      const MIN_HOLD_MS = 6000;
      const minHold = new Promise<void>((r) => setTimeout(r, MIN_HOLD_MS));
      let liveEstimates: ProfileMeasurements | null = seededMeasurements;
      let liveEstimatesUnit: "cm" | "in" = seededMeasurements ? seededMeasurementsUnit : "cm";
      if (!canUseSeededMeasurements && onEstimate && photoBase64) {
        const heightRaw = unit === "in"
          ? (parseInt(heightFt, 10) || 0) * 12 + (parseInt(heightInch, 10) || 0)
          : parseFloat(heightVal);
        try {
          const bodyLandmarks = await detectProfileBodyLandmarks();
          if (!hasUsableLegLandmarks(bodyLandmarks)) {
            setEstimating(false);
            setImageStep("name-photo");
            setError(t("Please upload a clear full-body photo with both legs and feet visible."));
            return;
          }
          const result = await onEstimate({
            photoBase64,
            height: heightRaw || 0,
            weight: w || 0,
            heightUnit: unit,
            weightUnit: unit === "in" ? "lbs" : "kg",
            gender,
            ...(a > 0 ? { age: a } : {}),
            ...(isWomen && bandSize ? { bandSize } : {}),
            ...(isWomen && cupSize ? { cupSize } : {}),
            ...(isWomen ? { braSizeRegion } : {}),
            bodyLandmarks: (bodyLandmarks as unknown as Record<string, { x: number; y: number }>) ?? undefined,
          });
          if (result?.estimates) {
            const nextEstimateUnit = result.unit === "in" ? "in" : "cm";
            liveEstimates = result.estimates as ProfileMeasurements;
            liveEstimatesUnit = nextEstimateUnit;
            setEstimateResults(result.estimates);
            setEstimateUnit(nextEstimateUnit);
          }
        } catch { /* non-fatal */ }
      }
      await minHold;
      setEstimating(false);
      if (!liveEstimates) {
        setError(t("Estimation could not complete"));
        return;
      }
      setEstimateResults(liveEstimates as Record<string, number>);
      setEstimateUnit(liveEstimatesUnit);
      return;
    }
    if (imageStep === "details") {
      // Legacy path — kept so any external caller that still routes
      // through "details" doesn't break. Simply advances to calculating.
      setImageStep("calculating");
      return;
    }
    // "calculating" done → save profile with estimates
    saveImageProfile();
  };

  useEffect(() => {
    if (!directAnalysisFlow || directAutoStartedRef.current) return;
    if (mode !== "image" || imageStep !== "name-photo") return;
    const hasHeight = unit === "in"
      ? (parseFloat(heightFt) > 0 || parseFloat(heightInch) > 0)
      : parseFloat(heightVal) > 0;
    const hasWeight = parseFloat(weightVal) > 0;
    const canUseSeededMeasurements = directAnalysisFlow && !!seededMeasurements;
    if (!photoBase64 || !name.trim() || (!canUseSeededMeasurements && (!hasHeight || !hasWeight))) return;
    directAutoStartedRef.current = true;
    void advanceImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directAnalysisFlow, mode, imageStep, photoBase64, name, heightFt, heightInch, heightVal, weightVal, unit]);

  const goBackImage = () => {
    setError("");
    if (imageStep === "details") { setImageStep("name-photo"); return; }
    if (imageStep === "calculating" && !estimating) { setImageStep("name-photo"); return; }
    setMode(null);
    setImageStep("name-photo");
  };

  // ── Top-level back/cancel ──
  const handleBack = () => {
    if (mode === "manual") return goBackManual();
    if (mode === "image") return goBackImage();
    onCancel();
  };

  // ── Step header label + progress ──
  const headerLabel = (() => {
    if (mode == null) return t("Choose a method");
    if (mode === "manual") {
      const labels: Record<ManualStep, string> = {
        identity: t("Identity"),
        chest: t("Chest"),
        stomach: t("Stomach"),
        seat: t("Back & Seat"),
        hips: t("Hips"),
        bra: t("Bra"),
      };
      return `${String(manualStepIdx + 1).padStart(2, "0")} · ${labels[manualStep]}`;
    }
    return t("Analyze by photo");
  })();

  const progressPct = (() => {
    if (mode == null) return 0;
    if (mode === "manual") return Math.round(((manualStepIdx + 1) / manualStepCount) * 100);
    // image path: name(50%) + photo(50%)
    let pct = 0;
    if (name.trim()) pct += 50;
    if (photoBase64) pct += 50;
    return pct;
  })();

  // When the wizard is on the image-flow photo step, we hide the global
  // ps-tryon-back-btn rendered by PrimeStyleTryonInner — the wizard's own
  // footer Back button is enough, and stacking two Back buttons looks
  // cluttered. The marker class on the root flips a CSS rule that
  // hides the sibling back button.
  const hideGlobalBack = mode === "image" && imageStep === "name-photo" && !directAnalysisFlow;
  const hideStepHeader = directAnalysisFlow && mode === "image";
  const hideFooter = directAnalysisFlow && mode === "image" && imageStep === "calculating" && estimating;
  const hideFooterBack = directAnalysisFlow && mode === "image";

  const imagePrimaryLabel = (() => {
    const canUseSeededMeasurements = directAnalysisFlow && !!seededMeasurements;
    const heightOk = canUseSeededMeasurements || (unit === "in"
      ? parseFloat(heightFt) > 0 || parseFloat(heightInch) > 0
      : parseFloat(heightVal) > 0);
    const weightOk = canUseSeededMeasurements || parseFloat(weightVal) > 0;
    const braOk = !isWomen || directAnalysisFlow || (!!bandSize && !!cupSize);
    if (photoUploading) return t("Analyzing photo…");
    if (!photoBase64) return t("Upload a photo");
    if (!name.trim()) return t("Add a name for this profile");
    if (!heightOk) return t("Enter your height");
    if (!weightOk) return t("Enter your weight");
    if (!braOk) return t("Select bra band and cup");
    if (estimateResults) return t("Save Profile");
    return t("Analyze My Size");
  })();

  const handleImagePrimaryAction = () => {
    if (!name.trim()) {
      setNameShaking(true);
      nameInputRef.current?.focus();
      setError(t("Please enter a profile name"));
      return;
    }
    if (estimateResults) {
      saveImageProfile();
      return;
    }
    void advanceImage();
  };

  const profilePhotoDetails = (
    <div className="ps-pm-body-details ps-cpw-mobile-profile-details">
      <div className="ps-pm-body-details-head">
        <div>
          <h3>{t("Profile details")}</h3>
          <p className="ps-pm-body-details-sub">{t("Used to calibrate the AI from your photo.")}</p>
        </div>
      </div>

      <div className="ps-bp-system-toggle">
        <button className={`ps-bp-system-btn${unit === "cm" ? " ps-bp-system-active" : ""}`} onClick={handleSetUnitCm} type="button">{t("Metric")}</button>
        <button className={`ps-bp-system-btn${unit === "in" ? " ps-bp-system-active" : ""}`} onClick={handleSetUnitIn} type="button">{t("Imperial")}</button>
      </div>

      <div className="ps-bp-inline-fields ps-cpw-inline-fields ps-cpw-mobile-profile-fields">
        <div className="ps-bp-inline-row">
          <span className="ps-bp-inline-label">{t("NAME")}</span>
          <div className="ps-bp-inline-input-group">
            <input
              ref={nameInputRef}
              type="text"
              className={`ps-bp-inline-input${nameShaking ? " ps-cpw-shake" : ""}`}
              value={name}
              placeholder={t("e.g. My Photo Profile")}
              onChange={(e) => { setName(e.target.value); setError(""); }}
              onAnimationEnd={() => setNameShaking(false)}
            />
          </div>
        </div>
        <div className="ps-bp-inline-row">
          <span className="ps-bp-inline-label">{t("GENDER")}</span>
          <div className="ps-bp-inline-input-group ps-cpw-gender-group">
            <button
              type="button"
              onClick={() => { setGender("male"); setBandSize(null); setCupSize(null); setError(""); }}
              className="ps-cpw-gender-pill"
              data-active={gender === "male" ? "true" : "false"}
            >{t("Male")}</button>
            <button
              type="button"
              onClick={() => { setGender("female"); setError(""); }}
              className="ps-cpw-gender-pill"
              data-active={gender === "female" ? "true" : "false"}
            >{t("Female")}</button>
          </div>
        </div>
        <div className="ps-bp-inline-row">
          <span className="ps-bp-inline-label">{t("HEIGHT")}</span>
          {unit === "cm" ? (
            <div className="ps-bp-inline-input-group">
              <input
                type="text"
                inputMode="decimal"
                className="ps-bp-inline-input"
                value={heightVal}
                placeholder={t("e.g. 173")}
                onChange={(e) => { setHeightVal(cleanNumericText(e.target.value, true)); setError(""); }}
              />
              <span className="ps-bp-inline-unit">cm</span>
            </div>
          ) : (
            <div className="ps-bp-inline-input-group ps-cpw-height-parts">
              <input
                type="text"
                inputMode="numeric"
                className="ps-bp-inline-input"
                value={heightFt}
                placeholder="5"
                onChange={(e) => { setHeightFt(cleanNumericText(e.target.value)); setError(""); }}
              />
              <span className="ps-bp-inline-unit">ft</span>
              <input
                type="text"
                inputMode="numeric"
                className="ps-bp-inline-input"
                value={heightInch}
                placeholder="8"
                onChange={(e) => { setHeightInch(cleanNumericText(e.target.value)); setError(""); }}
              />
              <span className="ps-bp-inline-unit">in</span>
            </div>
          )}
        </div>
        <div className="ps-bp-inline-row">
          <span className="ps-bp-inline-label">{t("WEIGHT")}</span>
          <div className="ps-bp-inline-input-group">
            <input
              type="text"
              inputMode="decimal"
              className="ps-bp-inline-input"
              value={weightVal}
              placeholder={unit === "in" ? t("e.g. 154") : t("e.g. 70")}
              onChange={(e) => { setWeightVal(cleanNumericText(e.target.value, true)); setError(""); }}
            />
            <span className="ps-bp-inline-unit">{unit === "in" ? "lbs" : "kg"}</span>
          </div>
        </div>
        {isWomen && (
          <div ref={inlineBraWrapRef}>
            <div className="ps-bp-inline-row">
              <span className="ps-bp-inline-label">{t("BRA REGION")}</span>
              <div className="ps-bp-inline-input-group">
                <MiniSelect
                  value={braSizeRegion}
                  options={Object.keys(BAND_SIZES)}
                  isOpen={inlineBraOpen === "region"}
                  onToggle={() => setInlineBraOpen(inlineBraOpen === "region" ? null : "region")}
                  onSelect={(v) => { setBraSizeRegion(v); setBandSize(null); setCupSize(null); setInlineBraOpen(null); setError(""); }}
                  direction="down"
                />
              </div>
            </div>
            <div className="ps-bp-inline-row">
              <span className="ps-bp-inline-label">{t("BAND")}</span>
              <div className="ps-bp-inline-input-group">
                <MiniSelect
                  value={bandSize}
                  options={BAND_SIZES[braSizeRegion] || BAND_SIZES.US}
                  placeholder="—"
                  isOpen={inlineBraOpen === "band"}
                  onToggle={() => setInlineBraOpen(inlineBraOpen === "band" ? null : "band")}
                  onSelect={(v) => { setBandSize(v); setInlineBraOpen(null); setError(""); }}
                  direction="down"
                />
              </div>
            </div>
            <div className="ps-bp-inline-row">
              <span className="ps-bp-inline-label">{t("CUP")}</span>
              <div className="ps-bp-inline-input-group">
                <MiniSelect
                  value={cupSize}
                  options={CUP_SIZES[braSizeRegion] || CUP_SIZES.US}
                  placeholder="—"
                  isOpen={inlineBraOpen === "cup"}
                  onToggle={() => setInlineBraOpen(inlineBraOpen === "cup" ? null : "cup")}
                  onSelect={(v) => { setCupSize(v); setInlineBraOpen(null); setError(""); }}
                  direction="down"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const photoHelpDrawer = !photoBase64 && photoHelpOpen ? (
    <>
      <div className="ps-bp-photo-help-backdrop" onClick={() => setPhotoHelpOpen(false)} aria-hidden="true" />
      <div role="dialog" aria-label={t("How to take the best photo")} className="ps-bp-photo-help">
        <div className="ps-bp-photo-help-handle" aria-hidden="true" />
        <div className="ps-bp-photo-help-header">
          <span className="ps-bp-photo-help-title">{t("How to take the best photo")}</span>
          <button type="button" onClick={() => setPhotoHelpOpen(false)} aria-label={t("Close")} className="ps-bp-photo-help-close">×</button>
        </div>
        <div className="ps-bp-photo-help-section ps-bp-photo-help-do">
          <div className="ps-bp-photo-help-section-label">✓ {t("Do")}</div>
          <div className="ps-bp-photo-help-section-body">
            {t("Stand facing the camera with your full body in frame")}<br />
            {t("Hold the phone at WAIST height (not eye level)")}<br />
            {t("Stand with your HEELS against a wall for best accuracy")}<br />
            {t("Use natural or even lighting (e.g. near a window)")}<br />
            {t("Wear fitted, simple clothing (e.g. neutral colors)")}<br />
            {t("Choose a plain background (a light wall is ideal)")}<br />
            {t("Stand straight and still, arms relaxed by your sides")}
          </div>
        </div>
        <div className="ps-bp-photo-help-section ps-bp-photo-help-dont">
          <div className="ps-bp-photo-help-section-label">✗ {t("Don't")}</div>
          <div className="ps-bp-photo-help-section-body">
            {t("Don't wear loose, baggy, or layered clothing")}<br />
            {t("Don't sit, pose, or bend your body")}<br />
            {t("Don't use strong backlighting")}<br />
            {t("Don't take mirror photos or selfies")}<br />
            {t("Don't apply filters, effects, or edits")}
          </div>
        </div>
        <div className="ps-bp-photo-help-section ps-bp-photo-help-tip">
          <strong>💡 {t("Quick Tip")}:</strong> {t("The simpler your photo is, the more accurate your virtual try-on results will be.")}
        </div>
      </div>
    </>
  ) : null;

  const imageReviewProfile = mode === "image" && imageStep === "calculating" && !estimating
    ? buildImageReviewProfile()
    : null;

  if (imageReviewProfile) {
    return (
      <div className={`ps-cpw-root ps-cpw-review-flow${isMobile ? " ps-cpw-mobile-profile-flow" : ""}${directAnalysisFlow ? " ps-cpw-direct-flow" : ""}`}>
        <ProfileMeasurementsView
          profile={imageReviewProfile}
          isActive={false}
          draftMode
          primaryLabel="Save Profile"
          isEstimating={false}
          onSelect={() => undefined}
          onDelete={() => undefined}
          onBack={() => { setImageStep("name-photo"); setError(""); }}
          onSaveMeasurements={(measurements, nextUnit) => {
            setEstimateResults(measurements as Record<string, number>);
            if (nextUnit) setEstimateUnit(nextUnit);
          }}
          onSaveBraSize={(nextBandSize, nextCupSize) => {
            setBandSize(nextBandSize || null);
            setCupSize(nextCupSize || null);
          }}
          onSave={(measurements, nextUnit, profileDetails) => saveImageProfile(measurements, nextUnit, profileDetails)}
          t={t}
        />
      </div>
    );
  }

  if (isMobile && mode === "image" && imageStep === "calculating" && estimating) {
    return (
      <div className="ps-cpw-root ps-cpw-direct-flow ps-cpw-mobile-profile-flow">
        <MobileScanningView
          previewUrl={photoBase64 ?? undefined}
          bodyLandmarks={scanLandmarks ?? null}
          sizingDone={!estimating}
          t={t}
        />
      </div>
    );
  }

  if (isMobile && mode === "image" && imageStep === "name-photo") {
    return (
      <div className={`ps-bp-wrapper ps-cpw-root ps-cpw-direct-flow ps-cpw-mobile-profile-flow${hideGlobalBack ? " ps-cpw-hide-global-back" : ""}`}>
        <div className="ps-bp-scan-progress">
          <span className="ps-bp-scan-progress-spacer" aria-hidden="true" />
          <div className="ps-bp-scan-progress-track">
            <div className="ps-bp-scan-progress-step ps-active">
              <span className="ps-bp-scan-progress-dot">1</span>
              <span className="ps-bp-scan-progress-label">{t("Photo")}</span>
            </div>
            <div className="ps-bp-scan-progress-line" />
            <div className="ps-bp-scan-progress-step">
              <span className="ps-bp-scan-progress-dot">2</span>
              <span className="ps-bp-scan-progress-label">{t("Details")}</span>
            </div>
          </div>
          <span className="ps-bp-scan-progress-spacer" aria-hidden="true" />
        </div>

        <PhotoStepMobile
          photoPreview={photoBase64}
          handlePhotoSelect={handlePhotoSelect}
          handleRemovePhoto={handleRemovePhoto}
          onAnalyze={handleImagePrimaryAction}
          onSwitchToManual={() => setMode("manual")}
          error={error}
          ageConfirmed={ageConfirmed}
          onAgeConfirmedChange={(v) => { setAgeConfirmed(v); if (v === true) setError(""); }}
          primaryLabel={imagePrimaryLabel}
          compactAgeGate
          photoProcessing={photoUploading}
          photoStatus={photoStatus}
          photoRejection={photoRejection}
          onClearRejection={() => setPhotoRejection(null)}
          photoHelpSlot={(
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPhotoHelpOpen(v => !v); }}
              aria-label={t("How to take a good photo")}
              className={`ps-bp-photo-help-chip${photoHelpOpen ? " ps-active" : ""}`}
            >
              <span className="ps-bp-photo-help-chip-mark" aria-hidden="true">?</span>
              <span>{t("How to take a good photo")}</span>
            </button>
          )}
          hideTabs
          t={t}
        />
        {profilePhotoDetails}
        {error && <p className="ps-bpm-error">{error}</p>}
        {photoHelpDrawer}
      </div>
    );
  }

  return (
    <div className={`ps-cpw-root${hideGlobalBack ? " ps-cpw-hide-global-back" : ""}${directAnalysisFlow ? " ps-cpw-direct-flow" : ""}`}>
      {/* Step header — hidden on the photo step so the split layout gets
          the full vertical space (the user already knows they're on the
          photo step from the Back button + content). */}
      {!hideStepHeader && !(mode === "image" && imageStep === "name-photo") && (
        <div className="ps-cpw-step-head">
          <h2 className="ps-cpw-step-title">{headerLabel}</h2>
          {mode != null && (
            <div className="ps-cpw-progress">
              <div className="ps-cpw-progress-track">
                <div className="ps-cpw-progress-fill" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Body ── */}
      <div className="ps-cpw-body" ref={bodyScrollRef}>
        {/* ── METHOD PICKER ── */}
        {mode == null && (
          <div className="ps-cpw-method-picker ps-cpw-fade-in" key="method-picker">
            <div className="ps-cpw-method-grid">
              <button
                type="button"
                className="ps-cpw-method-card"
                onClick={() => { setMode("manual"); setManualStep("identity"); }}
              >
                <span className="ps-cpw-method-num">01</span>
                <div className="ps-cpw-method-icon-badge">
                  <img src={methodManualImg} alt="" aria-hidden="true" />
                </div>
                <div className="ps-cpw-method-eyebrow">{t("Quick Setup")}</div>
                <div className="ps-cpw-method-title">{t("Measure manually")}</div>
                <div className="ps-cpw-method-desc">
                  {t("Enter height, weight and answer quick body shape questions.")}
                </div>
                <div className="ps-cpw-method-arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </div>
              </button>

              <button
                type="button"
                className="ps-cpw-method-card"
                onClick={() => { setMode("image"); }}
              >
                <span className="ps-cpw-method-num">02</span>
                <div className="ps-cpw-method-icon-badge">
                  <img src={methodPhotoImg} alt="" aria-hidden="true" />
                </div>
                <div className="ps-cpw-method-eyebrow">{t("AI Powered")}</div>
                <div className="ps-cpw-method-title">{t("Analyze by photo")}</div>
                <div className="ps-cpw-method-desc">
                  {t("Upload a full-body photo. Our AI computes every measurement.")}
                </div>
                <div className="ps-cpw-method-arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* ── MANUAL PATH ── */}
        {mode === "manual" && manualStep === "identity" && (
          <div key="manual-identity" className="ps-cpw-identity ps-cpw-fade-in">
            <div className="ps-cpw-pill-row">
              <div className="ps-cpw-pill-group">
                <button type="button" className={`ps-cpw-pill${gender === "male" ? " ps-active" : ""}`} onClick={() => setGender("male")}>{t("Male")}</button>
                <button type="button" className={`ps-cpw-pill${gender === "female" ? " ps-active" : ""}`} onClick={() => setGender("female")}>{t("Female")}</button>
              </div>
              <div className="ps-cpw-pill-group">
                <button type="button" className={`ps-cpw-pill${unit === "cm" ? " ps-active" : ""}`} onClick={handleSetUnitCm}>cm</button>
                <button type="button" className={`ps-cpw-pill${unit === "in" ? " ps-active" : ""}`} onClick={handleSetUnitIn}>in</button>
              </div>
            </div>

            {/* Inline rows mirroring the SDK's first basics screen — labels
                on the left, single underlined input on the right. */}
            <div className="ps-bp-inline-fields ps-cpw-inline-fields">
              <div className="ps-bp-inline-row">
                <span className="ps-bp-inline-label">{t("NAME")}</span>
                <div className="ps-bp-inline-input-group">
                  <input
                    type="text"
                    className="ps-bp-inline-input"
                    value={name}
                    placeholder={t("e.g. My Measurements")}
                    onChange={(e) => { setName(e.target.value); setError(""); }}
                  />
                </div>
              </div>
              <div className="ps-bp-inline-row">
                <span className="ps-bp-inline-label">{t("HEIGHT")}</span>
                {unit === "cm" ? (
                  <div className="ps-bp-inline-input-group">
                    <input
                      type="text" inputMode="decimal"
                      className="ps-bp-inline-input"
                      value={heightVal}
                      placeholder={t("e.g. 173")}
                      onChange={(e) => { setHeightVal(cleanNumericText(e.target.value, true)); setError(""); }}
                    />
                    <span className="ps-bp-inline-unit">cm</span>
                  </div>
                ) : (
                  <div className="ps-bp-inline-input-group" style={{ gap: "6px" }}>
                    <input
                      type="text" inputMode="numeric"
                      className="ps-bp-inline-input"
                      style={{ width: "3em" }}
                      value={heightFt}
                      placeholder="5"
                      onChange={(e) => { setHeightFt(cleanNumericText(e.target.value)); setError(""); }}
                    />
                    <span className="ps-bp-inline-unit">ft</span>
                    <input
                      type="text" inputMode="numeric"
                      className="ps-bp-inline-input"
                      style={{ width: "3em" }}
                      value={heightInch}
                      placeholder="8"
                      onChange={(e) => { setHeightInch(cleanNumericText(e.target.value)); setError(""); }}
                    />
                    <span className="ps-bp-inline-unit">in</span>
                  </div>
                )}
              </div>
              <div className="ps-bp-inline-row">
                <span className="ps-bp-inline-label">{t("WEIGHT")}</span>
                <div className="ps-bp-inline-input-group">
                  <input
                    type="text" inputMode="decimal"
                    className="ps-bp-inline-input"
                    value={weightVal}
                    placeholder={unit === "in" ? t("e.g. 154") : t("e.g. 70")}
                    onChange={(e) => { setWeightVal(cleanNumericText(e.target.value, true)); setError(""); }}
                  />
                  <span className="ps-bp-inline-unit">{unit === "in" ? "lbs" : "kg"}</span>
                </div>
              </div>
              <div className="ps-bp-inline-row">
                <span className="ps-bp-inline-label">{t("AGE")}</span>
                <div className="ps-bp-inline-input-group">
                  <input
                    type="text" inputMode="numeric"
                    className="ps-bp-inline-input"
                    value={ageVal}
                    placeholder={t("e.g. 30")}
                    onChange={(e) => { setAgeVal(cleanNumericText(e.target.value)); setError(""); }}
                  />
                  <span className="ps-bp-inline-unit">{t("years")}</span>
                </div>
              </div>

              {/* Inline bra band/cup picker for women — mirrors the
                  SDK's photo-path right column so users get to size
                  selection without an extra "bra" step in the flow. */}
              {isWomen && (
                <div ref={inlineBraWrapRef}>
                  <div className="ps-bp-inline-row">
                    <span className="ps-bp-inline-label">{t("BRA REGION")}</span>
                    <div className="ps-bp-inline-input-group">
                      <MiniSelect
                        value={braSizeRegion}
                        options={Object.keys(BAND_SIZES)}
                        isOpen={inlineBraOpen === "region"}
                        onToggle={() => setInlineBraOpen(inlineBraOpen === "region" ? null : "region")}
                        onSelect={(v) => { setBraSizeRegion(v); setBandSize(null); setCupSize(null); setInlineBraOpen(null); setError(""); }}
                        direction="up"
                      />
                    </div>
                  </div>
                  <div className="ps-bp-inline-row">
                    <span className="ps-bp-inline-label">{t("BAND")}</span>
                    <div className="ps-bp-inline-input-group">
                      <MiniSelect
                        value={bandSize}
                        options={BAND_SIZES[braSizeRegion] || BAND_SIZES.US}
                        placeholder="—"
                        isOpen={inlineBraOpen === "band"}
                        onToggle={() => setInlineBraOpen(inlineBraOpen === "band" ? null : "band")}
                        onSelect={(v) => { setBandSize(v); setInlineBraOpen(null); setError(""); }}
                        direction="up"
                      />
                    </div>
                  </div>
                  <div className="ps-bp-inline-row">
                    <span className="ps-bp-inline-label">{t("CUP")}</span>
                    <div className="ps-bp-inline-input-group">
                      <MiniSelect
                        value={cupSize}
                        options={CUP_SIZES[braSizeRegion] || CUP_SIZES.US}
                        placeholder="—"
                        isOpen={inlineBraOpen === "cup"}
                        onToggle={() => setInlineBraOpen(inlineBraOpen === "cup" ? null : "cup")}
                        onSelect={(v) => { setCupSize(v); setInlineBraOpen(null); setError(""); }}
                        direction="up"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {error && <div className="ps-cpw-error">{error}</div>}
          </div>
        )}

        {mode === "manual" && manualStep === "chest" && !isWomen && (
          <ProgressiveStep
            stepKey="chest"
            title={t("CHEST PROFILE")}
            subtitle={t("Select the profile that best matches your chest breadth")}
          >
            <ShapeCard img={imgs.chest?.narrow ?? null} label={t("NARROW")} selected={chestProfile === "narrow"} onSelect={() => pickShape("chest", "narrow")} />
            <ShapeCard img={imgs.chest?.average ?? null} label={t("AVERAGE")} selected={chestProfile === "average"} onSelect={() => pickShape("chest", "average")} />
            <ShapeCard img={imgs.chest?.broad ?? null} label={t("BROAD")} selected={chestProfile === "broad"} onSelect={() => pickShape("chest", "broad")} />
          </ProgressiveStep>
        )}

        {mode === "manual" && manualStep === "stomach" && (
          <ProgressiveStep
            stepKey="stomach"
            title={t("STOMACH PROFILE")}
            subtitle={t("Describe your mid-section silhouette")}
          >
            <ShapeCard img={imgs.stomach?.flat ?? null} label={t("FLAT")} selected={midsectionProfile === "flat"} onSelect={() => pickShape("stomach", "flat")} />
            <ShapeCard img={imgs.stomach?.average ?? null} label={t("AVERAGE")} selected={midsectionProfile === "average"} onSelect={() => pickShape("stomach", "average")} />
            <ShapeCard img={imgs.stomach?.round ?? null} label={t("ROUND")} selected={midsectionProfile === "round"} onSelect={() => pickShape("stomach", "round")} />
          </ProgressiveStep>
        )}

        {mode === "manual" && manualStep === "seat" && (
          <ProgressiveStep
            stepKey="seat"
            title={t("BACK & SEAT")}
            subtitle={t("The curvature of your lower spine and seat profile")}
          >
            <ShapeCard img={imgs.seat?.narrow ?? null} label={t("STRAIGHT")} selected={seatProfile === "narrow"} onSelect={() => pickShape("seat", "narrow")} />
            <ShapeCard img={imgs.seat?.average ?? null} label={t("CURVED")} selected={seatProfile === "average"} onSelect={() => pickShape("seat", "average")} />
            <ShapeCard img={imgs.seat?.full ?? null} label={t("DEEP")} selected={seatProfile === "full"} onSelect={() => pickShape("seat", "full")} />
          </ProgressiveStep>
        )}

        {mode === "manual" && manualStep === "hips" && isWomen && imgs.hips && (
          <ProgressiveStep
            stepKey="hips"
            title={t("HIP PROFILE")}
            subtitle={t("The curvature of your hips and thighs")}
          >
            <ShapeCard img={imgs.hips.narrow ?? null} label={t("NARROW")} selected={hipProfile === "narrow"} onSelect={() => pickShape("hips", "narrow")} />
            <ShapeCard img={imgs.hips.average ?? null} label={t("AVERAGE")} selected={hipProfile === "average"} onSelect={() => pickShape("hips", "average")} />
            <ShapeCard img={imgs.hips.full ?? null} label={t("FULL")} selected={hipProfile === "full"} onSelect={() => pickShape("hips", "full")} />
          </ProgressiveStep>
        )}

        {mode === "manual" && manualStep === "bra" && isWomen && (
          <div key="bra" className="ps-cpw-progressive ps-cpw-fade-in">
            <div className="ps-cpw-section-head">
              <div className="ps-cpw-section-title">{t("BRA SIZE")}</div>
              <div className="ps-cpw-section-sub">{t("Helps us refine bust measurements for tops and dresses")}</div>
            </div>
            <div className="ps-cpw-bra-row">
              <div className="ps-cpw-bra-field">
                <label className="ps-cpw-field-label">{t("BAND")}</label>
                <select
                  className="ps-cpw-input"
                  value={bandSize ?? ""}
                  onChange={(e) => { setBandSize(e.target.value || null); setError(""); }}
                >
                  <option value="">{t("Select")}</option>
                  {BAND_OPTIONS_US.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
              <div className="ps-cpw-bra-field">
                <label className="ps-cpw-field-label">{t("CUP")}</label>
                <select
                  className="ps-cpw-input"
                  value={cupSize ?? ""}
                  onChange={(e) => { setCupSize(e.target.value || null); setError(""); }}
                >
                  <option value="">{t("Select")}</option>
                  {CUP_OPTIONS_US.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {mode === "manual" && error && <div className="ps-cpw-error">{error}</div>}

        {/* ── IMAGE PATH — split layout: dropzone left, inputs + guide right ── */}
        {/* ── IMAGE PATH — Step 1: Name + Photo upload ── */}
        {mode === "image" && imageStep === "name-photo" && (
          <div key="image-photo" className="ps-cpw-fade-in ps-cpw-photo-step" style={{ display: "flex", gap: "1.2vw", padding: "1.5vw", width: "100%", height: "100%", minHeight: "20vw", minBlockSize: 0, alignItems: "stretch", position: "relative", overflow: "hidden", overscrollBehavior: "contain" }}>
            <div className="ps-cpw-photo-upload-col" style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column", gap: "0.7vw" }}>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/avif"
                style={{ display: "none" }}
                onChange={handlePhotoSelect}
              />
              <div
                onClick={() => { if (!photoBase64 && ageConfirmed === true) photoInputRef.current?.click(); }}
                onMouseEnter={() => { if (!photoBase64 && ageConfirmed === true) setUploadHoverCpw(true); }}
                onMouseLeave={() => setUploadHoverCpw(false)}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"; }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  if (photoBase64 || ageConfirmed !== true) return;
                  const file = e.dataTransfer?.files?.[0];
                  if (!file || !photoInputRef.current) return;
                  const dt = new DataTransfer();
                  dt.items.add(file);
                  photoInputRef.current.files = dt.files;
                  photoInputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
                }}
                style={{
                  flex: 1, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  border: photoBase64
                    ? "2px solid var(--ps-accent)"
                    : (ageConfirmed === true && uploadHoverCpw)
                      ? "2px dashed var(--ps-accent)"
                      : "2px dashed var(--ps-border-color)",
                  borderRadius: "0.5vw",
                  cursor: photoBase64 ? "default" : (ageConfirmed === true ? "pointer" : "default"),
                  position: "relative",
                  background: photoBase64
                    ? "var(--ps-bg-secondary)"
                    : (ageConfirmed === true && uploadHoverCpw)
                      ? "rgba(33,84,239,0.04)"
                      : ageConfirmed === null
                        ? "rgba(0,0,0,0.025)"
                        : "var(--ps-bg-secondary)",
                  transition: "border-color 0.18s, background 0.18s",
                  overflow: "hidden",
                }}
              >
                {!photoBase64 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPhotoHelpOpen(v => !v); }}
                    aria-label={t("How to take a good photo")}
                    title={t("How to take a good photo")}
                    className={`ps-bp-photo-help-chip${photoHelpOpen ? " ps-active" : ""}`}
                  >
                    <span className="ps-bp-photo-help-chip-mark" aria-hidden="true">?</span>
                    <span>{t("How to take a good photo")}</span>
                  </button>
                )}

                {!photoBase64 && photoHelpOpen && (
                  <div
                    role="dialog"
                    aria-label={t("How to take the best photo")}
                    className="ps-bp-photo-help ps-bp-photo-help-upload"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="ps-bp-photo-help-handle" aria-hidden="true" />
                    <div className="ps-bp-photo-help-header">
                      <span className="ps-bp-photo-help-title">{t("How to take the best photo")}</span>
                      <button
                        type="button"
                        onClick={() => setPhotoHelpOpen(false)}
                        aria-label={t("Close")}
                        className="ps-bp-photo-help-close"
                      >
                        ×
                      </button>
                    </div>
                    <div className="ps-bp-photo-help-section ps-bp-photo-help-do">
                      <div className="ps-bp-photo-help-section-label">✓ {t("Do")}</div>
                      <div className="ps-bp-photo-help-section-body">
                        {t("Stand facing the camera with your full body in frame")}<br />
                        {t("Hold the phone at WAIST height (not eye level)")}<br />
                        {t("Stand with your HEELS against a wall for best accuracy")}<br />
                        {t("Use natural or even lighting (e.g. near a window)")}<br />
                        {t("Wear fitted, simple clothing (e.g. neutral colors)")}<br />
                        {t("Choose a plain background (a light wall is ideal)")}<br />
                        {t("Stand straight and still, arms relaxed by your sides")}
                      </div>
                    </div>
                    <div className="ps-bp-photo-help-section ps-bp-photo-help-dont">
                      <div className="ps-bp-photo-help-section-label">✗ {t("Don't")}</div>
                      <div className="ps-bp-photo-help-section-body">
                        {t("Don't wear loose, baggy, or layered clothing")}<br />
                        {t("Don't sit, pose, or bend your body")}<br />
                        {t("Don't use strong backlighting")}<br />
                        {t("Don't take mirror photos or selfies")}<br />
                        {t("Don't apply filters, effects, or edits")}
                      </div>
                    </div>
                    <div className="ps-bp-photo-help-section ps-bp-photo-help-tip">
                      <strong>💡 {t("Quick Tip")}:</strong> {t("The simpler your photo is, the more accurate your virtual try-on results will be.")}
                    </div>
                  </div>
                )}

                {photoUploading && (
                  <div className="ps-bp-photo-overlay" onClick={e => e.stopPropagation()}>
                    <div className="ps-bp-photo-overlay-spinner" aria-hidden="true" />
                    <span className="ps-bp-photo-overlay-label">{photoStatus || t("Analyzing photo…")}</span>
                  </div>
                )}

                {photoBase64 ? (
                  <>
                    <img src={photoBase64} alt={t("preview")} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    <button onClick={e => { e.stopPropagation(); handleRemovePhoto(); }} style={{ position: "absolute", top: "0.5vw", right: "0.5vw", width: "1.4vw", height: "1.4vw", borderRadius: "50%", background: "rgba(0,0,0,0.6)", border: "none", color: "#fff", fontSize: "0.7vw", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>&times;</button>
                  </>
                ) : (
                  <>
                    <UploadIcon size={32} />
                    <span style={{ fontSize: "0.85vw", fontWeight: 600, color: "var(--ps-text-primary)", marginTop: "0.5vw" }}>{t("Upload your photo")}</span>
                    <span style={{ fontSize: "0.6vw", color: "var(--ps-text-muted)", marginTop: "0.2vw" }}>{t("Click or drag a full-body photo")}</span>
                    <span style={{ fontSize: "0.5vw", color: "var(--ps-text-dim)", marginTop: "0.4vw" }}>{t("JPEG, PNG, WebP, AVIF (max 10MB)")}</span>

                    {photoRejection && (
                      <div
                        role="alert"
                        onClick={e => e.stopPropagation()}
                        style={{
                          marginTop: "1.1vw",
                          padding: "0.75vw 1.1vw",
                          background: "#FFFFFF",
                          border: "1px solid rgba(220, 38, 38, 0.35)",
                          borderRadius: "0.55vw",
                          boxShadow: "0 6px 18px -8px rgba(220, 38, 38, 0.25)",
                          display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.55vw",
                          cursor: "default", maxWidth: "20vw",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "0.45vw" }}>
                          <svg viewBox="0 0 24 24" width="0.85vw" height="0.85vw" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                          </svg>
                          <span style={{ color: "#dc2626", fontSize: "0.7vw", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>{t("Different photo needed")}</span>
                        </div>
                        <div style={{ fontSize: "0.62vw", color: "var(--ps-text-secondary)", lineHeight: 1.55 }}>
                          {photoRejection}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setPhotoRejection(null); photoInputRef.current?.click(); }}
                          style={{
                            padding: "0.35vw 0.8vw",
                            background: "var(--ps-accent)",
                            color: "#fff",
                            border: "none",
                            borderRadius: "0.35vw",
                            fontSize: "0.62vw",
                            fontWeight: 700,
                            cursor: "pointer",
                            fontFamily: "inherit",
                          }}
                        >{t("Try another photo")}</button>
                      </div>
                    )}

                    {ageConfirmed === null && !photoRejection && (
                      <div
                        role="dialog"
                        aria-labelledby="ps-cpw-age-q"
                        onClick={e => e.stopPropagation()}
                        style={{
                          marginTop: "1.1vw",
                          padding: "0.75vw 1.1vw",
                          background: "#FFFFFF",
                          border: "1px solid var(--ps-border-color)",
                          borderRadius: "0.55vw",
                          boxShadow: "0 6px 18px -8px rgba(17,24,39,0.22)",
                          display: "flex", alignItems: "center", gap: "0.8vw",
                          cursor: "default",
                          animation: "ps-cpw-age-gate-in 0.28s ease-out both",
                        }}
                      >
                        <span id="ps-cpw-age-q" style={{ fontSize: "0.78vw", fontWeight: 600, color: "var(--ps-text-primary)" }}>
                          {t("Are you 18 or older?")}
                        </span>
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); setAgeConfirmed(true); setError(""); }}
                          style={{
                            padding: "0.4vw 1vw", borderRadius: "999px",
                            background: "var(--ps-accent)", color: "#FFFFFF",
                            border: "1px solid var(--ps-accent)",
                            fontFamily: "inherit", fontSize: "0.68vw", fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {t("Yes")}
                        </button>
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); setAgeConfirmed(false); }}
                          style={{
                            padding: "0.4vw 1vw", borderRadius: "999px",
                            background: "transparent", color: "var(--ps-text-secondary)",
                            border: "1px solid var(--ps-border-color)",
                            fontFamily: "inherit", fontSize: "0.68vw", fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {t("No")}
                        </button>
                      </div>
                    )}

                    {ageConfirmed === false && (
                      <div
                        role="alert"
                        onClick={e => e.stopPropagation()}
                        style={{
                          marginTop: "1.1vw",
                          padding: "0.75vw 1.1vw",
                          background: "#FFFFFF",
                          border: "1px solid rgba(192,38,38,0.4)",
                          borderRadius: "0.55vw",
                          boxShadow: "0 6px 18px -8px rgba(192,38,38,0.22)",
                          display: "flex", alignItems: "center", gap: "0.8vw",
                          cursor: "default",
                        }}
                      >
                        <span style={{ fontSize: "0.78vw", fontWeight: 600, color: "#C02626" }}>
                          {t("Under 18 — can't process this photo.")}
                        </span>
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); setAgeConfirmed(null); }}
                          style={{
                            padding: "0.4vw 1vw", borderRadius: "999px",
                            background: "transparent", color: "var(--ps-text-secondary)",
                            border: "1px solid var(--ps-border-color)",
                            fontFamily: "inherit", fontSize: "0.68vw", fontWeight: 700, cursor: "pointer",
                          }}
                        >
                          {t("Go back")}
                        </button>
                      </div>
                    )}

                    {ageConfirmed === true && (
                      <div
                        onClick={e => e.stopPropagation()}
                        style={{
                          position: "absolute", left: "0.8vw", right: "0.8vw", bottom: "0.6vw",
                          fontSize: "0.62vw", lineHeight: 1.5,
                          color: "var(--ps-text-secondary)",
                          textAlign: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3vw", verticalAlign: "middle" }}>
                          <svg viewBox="0 0 24 24" width="0.8vw" height="0.8vw" fill="none" stroke="#2154ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          </svg>
                          <span style={{ fontWeight: 700, color: "#2154ef", letterSpacing: "0.08em", textTransform: "uppercase" }}>{t("Legal Notice")}</span>
                        </span>
                        {" — "}
                        {t("Your image will be used to generate a virtual try-on preview showing how selected items may look and fit. Images are processed securely and are not stored after generation.")}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Soft vertical divider with gradient fade at top/bottom —
                same treatment as BodyProfileView. */}
            <div aria-hidden="true" style={{
              width: "1px",
              alignSelf: "stretch",
              background: "linear-gradient(to bottom, transparent 0%, var(--ps-border-subtle) 18%, var(--ps-border-subtle) 82%, transparent 100%)",
              flexShrink: 0,
            }} />
            <div className="ps-cpw-photo-details-col" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: "0.8vw", justifyContent: "flex-start", position: "relative", overflowY: "auto", overflowX: "visible", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}>
              {/* Header on row 1, subtitle on row 2. The photo-help control
                  stays on the upload/photo side only to avoid duplicate help
                  buttons across the split profile-creation layout. */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "0.6vw" }}>
                  <h3 style={{ margin: 0, fontSize: "1vw", fontWeight: 700, color: "var(--ps-text-primary)" }}>{t("Profile details")}</h3>
                </div>
                <p style={{ margin: "0.2vw 0 0", fontSize: "0.65vw", color: "var(--ps-text-muted)" }}>{t("Used to calibrate the AI from your photo.")}</p>
              </div>

              {/* Metric/Imperial toggle — pinned just below the header,
                  same as BodyProfileView. Unit choice is the first thing
                  the user sees so it doesn't drift mid-column. */}
              <div className="ps-bp-system-toggle" style={{ alignSelf: "center" }}>
                <button className={`ps-bp-system-btn${unit === "cm" ? " ps-bp-system-active" : ""}`} onClick={handleSetUnitCm} type="button">{t("Metric")}</button>
                <button className={`ps-bp-system-btn${unit === "in" ? " ps-bp-system-active" : ""}`} onClick={handleSetUnitIn} type="button">{t("Imperial")}</button>
              </div>

              {/* Inputs cluster — only the inputs are vertically centered
                  in the remaining column space (marginTop/Bottom: auto).
                  Header + toggle stay pinned above so they don't drift. */}
              <div className="ps-cpw-profile-fields-shell" style={{ marginTop: "auto", marginBottom: "auto", minHeight: 0, overscrollBehavior: "contain" }}>
              <div className="ps-bp-inline-fields ps-cpw-inline-fields">
                <div className="ps-bp-inline-row">
                  <span className="ps-bp-inline-label">{t("NAME")}</span>
                  <div className="ps-bp-inline-input-group">
                    <input ref={nameInputRef} type="text" className={`ps-bp-inline-input${nameShaking ? " ps-cpw-shake" : ""}`} value={name} placeholder={t("e.g. My Photo Profile")} onChange={(e) => { setName(e.target.value); setError(""); }} onAnimationEnd={() => setNameShaking(false)} />
                  </div>
                </div>
                <div className="ps-bp-inline-row">
                  <span className="ps-bp-inline-label">{t("GENDER")}</span>
                  <div className="ps-bp-inline-input-group ps-cpw-gender-group" style={{ gap: "0.4vw" }}>
                    <button
                      type="button"
                      onClick={() => { setGender("male"); setBandSize(null); setCupSize(null); setError(""); }}
                      className="ps-cpw-gender-pill"
                      data-active={gender === "male" ? "true" : "false"}
                    >{t("Male")}</button>
                    <button
                      type="button"
                      onClick={() => { setGender("female"); setError(""); }}
                      className="ps-cpw-gender-pill"
                      data-active={gender === "female" ? "true" : "false"}
                    >{t("Female")}</button>
                  </div>
                </div>
                <div className="ps-bp-inline-row">
                  <span className="ps-bp-inline-label">{t("HEIGHT")}</span>
                  {unit === "cm" ? (
                    <div className="ps-bp-inline-input-group">
                      <input type="text" inputMode="decimal" className="ps-bp-inline-input" value={heightVal} placeholder={t("e.g. 173")} onChange={(e) => { setHeightVal(cleanNumericText(e.target.value, true)); setError(""); }} />
                      <span className="ps-bp-inline-unit">cm</span>
                    </div>
                  ) : (
                    <div className="ps-bp-inline-input-group" style={{ gap: "6px" }}>
                      <input type="text" inputMode="numeric" className="ps-bp-inline-input" style={{ width: "3em" }} value={heightFt} placeholder="5" onChange={(e) => { setHeightFt(cleanNumericText(e.target.value)); setError(""); }} />
                      <span className="ps-bp-inline-unit">ft</span>
                      <input type="text" inputMode="numeric" className="ps-bp-inline-input" style={{ width: "3em" }} value={heightInch} placeholder="8" onChange={(e) => { setHeightInch(cleanNumericText(e.target.value)); setError(""); }} />
                      <span className="ps-bp-inline-unit">in</span>
                    </div>
                  )}
                </div>
                <div className="ps-bp-inline-row">
                  <span className="ps-bp-inline-label">{t("WEIGHT")}</span>
                  <div className="ps-bp-inline-input-group">
                    <input type="text" inputMode="decimal" className="ps-bp-inline-input" value={weightVal} placeholder={unit === "in" ? t("e.g. 154") : t("e.g. 70")} onChange={(e) => { setWeightVal(cleanNumericText(e.target.value, true)); setError(""); }} />
                    <span className="ps-bp-inline-unit">{unit === "in" ? "lbs" : "kg"}</span>
                  </div>
                </div>
                {/* Women: bra size inputs (region + band + cup). Required so the
                    backend can use it to compute bust deterministically instead
                    of guessing from BMI/shoulder ratio. Custom mini-select drawer
                    UI (animated slide-fade) instead of native dropdowns. */}
                {isWomen && (
                  <div ref={inlineBraWrapRef}>
                    <div className="ps-bp-inline-row">
                      <span className="ps-bp-inline-label">{t("BRA REGION")}</span>
                      <div className="ps-bp-inline-input-group">
                        <MiniSelect
                          value={braSizeRegion}
                          options={Object.keys(BAND_SIZES)}
                          isOpen={inlineBraOpen === "region"}
                          onToggle={() => setInlineBraOpen(inlineBraOpen === "region" ? null : "region")}
                          onSelect={(v) => { setBraSizeRegion(v); setBandSize(null); setCupSize(null); setInlineBraOpen(null); setError(""); }}
                          direction="up"
                        />
                      </div>
                    </div>
                    <div className="ps-bp-inline-row">
                      <span className="ps-bp-inline-label">{t("BAND")}</span>
                      <div className="ps-bp-inline-input-group">
                        <MiniSelect
                          value={bandSize}
                          options={BAND_SIZES[braSizeRegion] || BAND_SIZES.US}
                          placeholder="—"
                          isOpen={inlineBraOpen === "band"}
                          onToggle={() => setInlineBraOpen(inlineBraOpen === "band" ? null : "band")}
                          onSelect={(v) => { setBandSize(v); setInlineBraOpen(null); setError(""); }}
                          direction="up"
                        />
                      </div>
                    </div>
                    <div className="ps-bp-inline-row">
                      <span className="ps-bp-inline-label">{t("CUP")}</span>
                      <div className="ps-bp-inline-input-group">
                        <MiniSelect
                          value={cupSize}
                          options={CUP_SIZES[braSizeRegion] || CUP_SIZES.US}
                          placeholder="—"
                          direction="up"
                          isOpen={inlineBraOpen === "cup"}
                          onToggle={() => setInlineBraOpen(inlineBraOpen === "cup" ? null : "cup")}
                          onSelect={(v) => { setCupSize(v); setInlineBraOpen(null); setError(""); }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              </div>
              {/* Legal Notice was here — moved to under the upload zone (left
                  column) so it sits next to the photo it talks about, only
                  after the user confirms 18+. */}
              {error && <div className="ps-cpw-error">{error}</div>}
            </div>
          </div>
        )}

        {/* ── IMAGE PATH — Step 2: Height / Weight / Age (separate from photo) ── */}
        {mode === "image" && imageStep === "details" && (
          <div key="image-details" className="ps-cpw-fade-in" style={{ padding: "1vw 1.5vw" }}>
            <div className="ps-cpw-section-head">
              <div className="ps-cpw-section-title">{t("Your measurements")}</div>
              <div className="ps-cpw-section-sub">{t("Enter your details for accurate body estimation")}</div>
            </div>
            <div className="ps-cpw-pill-row" style={{ marginBottom: "0.8vw" }}>
              <div className="ps-cpw-pill-group">
                <button type="button" className={`ps-cpw-pill${unit === "cm" ? " ps-active" : ""}`} onClick={handleSetUnitCm}>cm</button>
                <button type="button" className={`ps-cpw-pill${unit === "in" ? " ps-active" : ""}`} onClick={handleSetUnitIn}>in</button>
              </div>
            </div>
            <div className="ps-bp-inline-fields ps-cpw-inline-fields">
              <div className="ps-bp-inline-row">
                <span className="ps-bp-inline-label">{t("HEIGHT")}</span>
                {unit === "cm" ? (
                  <div className="ps-bp-inline-input-group">
                    <input type="text" inputMode="decimal" className="ps-bp-inline-input" value={heightVal} placeholder={t("e.g. 173")} onChange={(e) => { setHeightVal(cleanNumericText(e.target.value, true)); setError(""); }} />
                    <span className="ps-bp-inline-unit">cm</span>
                  </div>
                ) : (
                  <div className="ps-bp-inline-input-group" style={{ gap: "6px" }}>
                    <input type="text" inputMode="numeric" className="ps-bp-inline-input" style={{ width: "3em" }} value={heightFt} placeholder="5" onChange={(e) => { setHeightFt(cleanNumericText(e.target.value)); setError(""); }} />
                    <span className="ps-bp-inline-unit">ft</span>
                    <input type="text" inputMode="numeric" className="ps-bp-inline-input" style={{ width: "3em" }} value={heightInch} placeholder="8" onChange={(e) => { setHeightInch(cleanNumericText(e.target.value)); setError(""); }} />
                    <span className="ps-bp-inline-unit">in</span>
                  </div>
                )}
              </div>
              <div className="ps-bp-inline-row">
                <span className="ps-bp-inline-label">{t("WEIGHT")}</span>
                <div className="ps-bp-inline-input-group">
                  <input type="text" inputMode="decimal" className="ps-bp-inline-input" value={weightVal} placeholder={unit === "in" ? t("e.g. 154") : t("e.g. 70")} onChange={(e) => { setWeightVal(cleanNumericText(e.target.value, true)); setError(""); }} />
                  <span className="ps-bp-inline-unit">{unit === "in" ? "lbs" : "kg"}</span>
                </div>
              </div>
              {/* Age intentionally NOT collected on the image-upload path —
                  the vision pipeline doesn't use it (the photo + height
                  + weight already fully constrain the body model). */}
            </div>
            {error && <div className="ps-cpw-error">{error}</div>}
          </div>
        )}

        {/* ── IMAGE PATH — Step 3: Calculating body measurements ── */}
        {mode === "image" && imageStep === "calculating" && (() => {
          // Stage sequence shown on the right while estimation runs —
          // matches the mobile MobileScanningView photo-mode cadence
          // verbatim so the user sees the same "READING → ESTIMATING →
          // MATCHING → FINALIZING" beat as the regular size flow.
          const stages: Array<{ title: string; desc: string }> = [
            { title: t("DETECTING POSE"),     desc: t("Identifying body landmarks from your photo.") },
            { title: t("SCANNING FRAME"),     desc: t("Our AI is mapping your proportions for a size suggestion.") },
            { title: t("ANALYZING BODY"),     desc: t("Measuring shoulders, chest, waist and hips.") },
            { title: t("MATCHING SIZE"),      desc: t("Comparing your measurements to the size guide.") },
            { title: t("FINALIZING RESULT"),  desc: t("Almost done — preparing your suggested size.") },
          ];
          const stage = stages[Math.min(scanStageIdx, stages.length - 1)] ?? stages[0]!;

          return (
          <div key="image-calculating" className="ps-cpw-fade-in" style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "auto" }}>
            {estimating ? (
              <div style={{ display: "flex", gap: "1.2vw", padding: "1.5vw", flex: 1 }}>
                {/* Left — photo with MediaPipe skeleton overlay */}
                <div style={{
                  flex: 1,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "var(--ps-bg-secondary)",
                  border: "1px solid var(--ps-border-subtle)",
                  borderRadius: "0.55vw",
                  position: "relative",
                  overflow: "hidden",
                  minHeight: "20vw",
                }}>
                  {photoBase64 && (
                    <img
                      src={photoBase64}
                      alt=""
                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                      onLoad={(e) => {
                        const img = e.currentTarget;
                        setScanImgDims({
                          w: img.naturalWidth || img.offsetWidth || 800,
                          h: img.naturalHeight || img.offsetHeight || 1200,
                        });
                      }}
                    />
                  )}
                  {scanLandmarks && (
                    <PoseOverlay
                      landmarks={scanLandmarks}
                      width={scanImgDims.w}
                      height={scanImgDims.h}
                      minVisibility={0.12}
                    />
                  )}
                </div>

                {/* Right — cycling status text */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "1vw", paddingLeft: "0.5vw" }}>
                  <div className="ps-tryon-size-loading-spinner" />
                  <div key={scanStageIdx % stages.length} style={{ animation: "ps-cpw-age-gate-in 0.38s ease-out both" }}>
                    <div style={{ fontSize: "0.7vw", fontWeight: 700, color: "var(--ps-accent)", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: "0.4vw" }}>
                      {stage.title}
                    </div>
                    <div style={{ fontSize: "1.05vw", fontWeight: 600, color: "var(--ps-text-primary)", lineHeight: 1.35 }}>
                      {stage.desc}
                    </div>
                  </div>
                  <div style={{ fontSize: "0.65vw", color: "var(--ps-text-muted)", marginTop: "0.4vw" }}>
                    {scanLandmarks
                      ? t("✓ Pose detected — running body analysis")
                      : t("Detecting pose landmarks…")}
                  </div>
                </div>
              </div>
            ) : estimateResults ? null : (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: "0.8vw" }}>
                <p style={{ color: "var(--ps-text-primary)", fontSize: "1vw", fontWeight: 600 }}>{t("Estimation could not complete")}</p>
                <p style={{ color: "var(--ps-text-muted)", fontSize: "0.75vw" }}>{t("Please try again or use manual measurements")}</p>
              </div>
            )}
          </div>
          );
        })()}
      </div>

      {/* ── Footer actions ── Back on the left edge, primary CTA on the
          right edge via the default justify-content: space-between layout. */}
      {!hideFooter && (
      <div className={`ps-cpw-footer${hideFooterBack ? " ps-cpw-footer-no-back" : ""}`}>
        {!hideFooterBack && (
          <button type="button" className="ps-cpw-back-btn" onClick={handleBack}>
            {mode == null ? t("Cancel") : `← ${t("Back")}`}
          </button>
        )}
        {/* Manual flow Next button is hidden on auto-advance shape steps —
            picking a card moves to the next step automatically. The button
            still shows on the identity + bra steps which need explicit
            confirmation (form inputs / dropdown selections). */}
        {mode === "manual" && !isAutoAdvanceStep && (
          <button type="button" className="ps-cpw-next-btn" onClick={advanceManual}>
            {isLastManualStep ? t("CALCULATE MY FIT") : t("Continue")}
          </button>
        )}
        {mode === "image" && imageStep === "name-photo" && (() => {
          // Dynamic label — show the user exactly what's missing before
          // the Calculate button activates. Order: photo → name → height
          // → weight → age → bra (women) → go.
          const heightOk = unit === "in"
            ? parseFloat(heightFt) > 0 || parseFloat(heightInch) > 0
            : parseFloat(heightVal) > 0;
          const weightOk = parseFloat(weightVal) > 0;
          const braOk = !isWomen || directAnalysisFlow || (!!bandSize && !!cupSize);
          const nameOk = !!name.trim();
          const photoOk = !!photoBase64;
          const analyzing = photoUploading;
          // After estimation completes the user lands back here to review
          // values; the button commits the profile in one click instead of
          // the previous "save → re-show inputs → save" double click.
          const reviewMode = !!estimateResults;
          const label = analyzing
            ? t("Analyzing photo…")
            : !photoOk
              ? t("Upload a photo")
              : !nameOk
                ? t("Add a name for this profile")
                : !heightOk
                  ? t("Enter your height")
                  : !weightOk
                    ? t("Enter your weight")
                    : !braOk
                      ? t("Select bra band and cup")
                      : reviewMode
                        ? t("Save Profile")
                        : t("Calculate My Body Parts");
          const disabled = analyzing || !photoOk || !nameOk || !heightOk || !weightOk || !braOk;
          return (
            <button
              type="button"
              className="ps-cpw-next-btn"
              onClick={() => {
                if (!nameOk) {
                  setNameShaking(true);
                  nameInputRef.current?.focus();
                  return;
                }
                if (reviewMode) {
                  // Review pass — skip the calculating step and commit.
                  saveImageProfile();
                  return;
                }
                advanceImage();
              }}
              disabled={disabled}
              title={disabled ? label : undefined}
            >
              {label}
            </button>
          );
        })()}
        {mode === "image" && imageStep === "details" && (
          <button type="button" className="ps-cpw-next-btn" onClick={advanceImage}>
            {t("Calculate My Body Parts")}
          </button>
        )}
        {mode === "image" && imageStep === "calculating" && !estimating && estimateResults && (
          <button type="button" className="ps-cpw-next-btn" onClick={advanceImage}>
            {t("Save Profile")}
          </button>
        )}
        {mode === "image" && imageStep === "calculating" && !estimating && !estimateResults && (
          <button type="button" className="ps-cpw-next-btn" onClick={() => { setImageStep("name-photo"); setError(""); }}>
            {t("Try Again")}
          </button>
        )}
      </div>
      )}
    </div>
  );
}
